import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { spaceOf } from "@/lib/spaces";

export const PageHeader = ({ title, subtitle, children }) => {
  const { space } = useApp();
  const s = spaceOf(space);
  return (
    <div className="fade-up mb-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: s.color }}>{s.key === "all" ? "All spaces" : `${s.name}'s space`}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-3 max-w-xl text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
};

export const PrimaryButton = ({ children, ...props }) => (
  <button {...props} className="flex h-10 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-white">
    {children}
  </button>
);

export const Segmented = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-full border bg-card/50 p-1" data-testid={testid}>
    {options.map((o) => (
      <button key={o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${value === o.value ? "bg-white/10 text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
        {o.label}
      </button>
    ))}
  </div>
);

export const ItemMenu = ({ onEdit, onDelete, testid }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button data-testid={`${testid}-menu`} className="rounded-md p-1.5 text-muted-foreground opacity-60 transition-[opacity,background-color] hover:bg-white/5 hover:text-foreground group-hover:opacity-100">
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuItem onClick={onEdit} data-testid={`${testid}-edit`} className="gap-2"><Pencil className="h-3.5 w-3.5" /> Edit</DropdownMenuItem>
      <DropdownMenuItem onClick={onDelete} data-testid={`${testid}-delete`} className="gap-2 text-red-400 focus:text-red-300"><Trash2 className="h-3.5 w-3.5" /> Delete</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

export const Empty = ({ icon: Icon, text, action, testid }) => (
  <div className="flex flex-col items-start gap-3 rounded-2xl border border-dashed px-6 py-10" data-testid={testid}>
    {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
    <p className="text-sm text-muted-foreground">{text}</p>
    {action}
  </div>
);

export const Panel = ({ title, count, action, children, className = "", testid }) => (
  <section className={`fade-up rounded-2xl border bg-card/70 p-5 sm:p-6 ${className}`} data-testid={testid}>
    <div className="mb-5 flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 font-display text-lg font-semibold tracking-tight">
        {title}
        {count != null && <span className="rounded-full bg-white/[0.06] px-2 py-0.5 font-mono text-xs text-muted-foreground">{count}</span>}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

export const Loading = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-white/[0.04]" />)}
  </div>
);
